import type { ResolvedTrack } from '@open-songster/shared'

/**
 * Orderings the desktop editor offers. `position` is the playlist's own order,
 * which is what the mobile app and the game use; every other key is a view.
 */
export type TrackSortKey = 'position' | 'year' | 'artist' | 'title' | 'genre' | 'album'

export type SortDirection = 'asc' | 'desc'

export interface TrackSort {
  key: TrackSortKey
  direction: SortDirection
}

/** The genre a track is filed under: Spotify lists the most relevant first. */
export function primaryGenre(track: Pick<ResolvedTrack, 'genres'>): string | null {
  const first = track.genres?.[0]
  return first !== undefined && first.trim().length > 0 ? first : null
}

const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true })

function compareNullable(
  a: string | number | null,
  b: string | number | null,
  direction: SortDirection,
): number {
  // Missing values sink to the bottom whichever way the column is sorted, so
  // the undated and the genre-less are always in one place to work through.
  if (a === null && b === null) return 0
  if (a === null) return 1
  if (b === null) return -1
  const result =
    typeof a === 'number' && typeof b === 'number' ? a - b : collator.compare(String(a), String(b))
  return direction === 'asc' ? result : -result
}

function sortValue(track: ResolvedTrack, key: TrackSortKey): string | number | null {
  switch (key) {
    case 'year':
      return track.year
    case 'artist':
      return track.artists[0] ?? null
    case 'title':
      return track.title
    case 'genre':
      return primaryGenre(track)
    case 'album':
      return track.albumName ?? null
    case 'position':
      return null
  }
}

/**
 * Sorts a copy of the tracks. Stable, so ties keep playlist order, and a
 * secondary sort on artist then title keeps a year's rows readable.
 */
export function sortTracks(tracks: readonly ResolvedTrack[], sort: TrackSort): ResolvedTrack[] {
  if (sort.key === 'position') {
    return sort.direction === 'asc' ? [...tracks] : [...tracks].reverse()
  }
  return [...tracks].sort((a, b) => {
    const primary = compareNullable(sortValue(a, sort.key), sortValue(b, sort.key), sort.direction)
    if (primary !== 0) return primary
    if (sort.key !== 'artist') {
      const byArtist = compareNullable(a.artists[0] ?? null, b.artists[0] ?? null, 'asc')
      if (byArtist !== 0) return byArtist
    }
    return compareNullable(a.title, b.title, 'asc')
  })
}

/** Case- and accent-insensitive substring match over title, artists, album and genres. */
export function trackMatches(track: ResolvedTrack, query: string): boolean {
  const needle = fold(query)
  if (needle.length === 0) return true
  const haystack = [
    track.title,
    track.originalTitle,
    ...track.artists,
    track.albumName,
    ...(track.genres ?? []),
  ]
    .filter((value): value is string => typeof value === 'string')
    .map(fold)
    .join(' ')
  return haystack.includes(needle)
}

function fold(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}
